import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ApiClient, type WeatherResponse } from '../../data/api'
import { useAuth } from '../../auth/authContext'
import { useRoadbook } from '../../data/roadbookContext'
import { activeStages } from '../../lib/trip'
import type { DiaryWeather, TrackFix } from '../../lib/types'
import { fetchWeatherForStages, stagesNeedingWeather, type WeatherJob } from './weatherRun'

/** Holt die Stundenwerte des Wetters entlang einer Fahrt. */
export type FetchWeather = (samples: TrackFix[], signal?: AbortSignal) => Promise<WeatherResponse>

/**
 * Der Zugang zum Wetterdienst, an die laufende Anmeldung gebunden.
 *
 * Ein eigener Client und nicht der des Abgleichs - dieselbe Überlegung wie bei
 * useFetchRoute in features/stages/routeApi.ts: der steckt in der Engine im
 * RoadbookProvider und hat dort nichts nach draussen zu geben. ApiClient hält
 * ausser der Tokenquelle keinen Zustand, ein zweiter kostet also nichts.
 */
export function useFetchWeather(): FetchWeather {
  const { getToken } = useAuth()
  return useMemo(() => {
    const client = new ApiClient(getToken)
    return (samples, signal) => client.fetchWeather(samples, signal)
  }, [getToken])
}

/**
 * Ob der Browser gerade eine Verbindung sieht.
 *
 * Eine eigene kleine Anzeige und nicht der Zustand des Abgleichs: dessen
 * 'offline' entsteht beim letzten Durchgang und kann eine Minute alt sein. Hier
 * hängt eine Beschriftung daran - „wartet auf Netz“ gegen „wird geholt“ -, und
 * die soll stimmen, sobald das Telefon wieder Empfang hat.
 *
 * navigator.onLine ist bekanntermassen optimistisch: es sagt „online“, sobald
 * irgendeine Schnittstelle steht, auch wenn dahinter nichts erreichbar ist. Für
 * diese Beschriftung reicht das - was wirklich passiert, sagt danach der
 * Versuch selbst.
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine)
  useEffect(() => {
    const auf = () => {
      setOnline(true)
    }
    const zu = () => {
      setOnline(false)
    }
    window.addEventListener('online', auf)
    window.addEventListener('offline', zu)
    return () => {
      window.removeEventListener('online', auf)
      window.removeEventListener('offline', zu)
    }
  }, [])
  return online
}

/** Was der Nachlauf nach aussen meldet. */
export interface WeatherNachlauf {
  /** Wie viele Etappen gerade noch offen sind. */
  offen: number
  /** Läuft gerade ein Durchgang? */
  laeuft: boolean
  /** Holt eine einzelne Etappe, auf Knopfdruck. */
  holen: (job: WeatherJob) => Promise<void>
}

/**
 * Holt das Wetter aller Etappen nach, für die es etwas zu holen gibt.
 *
 * Von selbst und nicht auf Knopfdruck, und das ist der Kern der Sache: die
 * GPX-Datei wird abends auf dem Platz eingelesen, oft im Funkloch. Dass für
 * diesen Tag Wetter abrufbar ist, steht danach im Bestand - es ist der Track
 * selbst, der es sagt -, und geholt wird es, sobald wieder Netz da ist. Kein
 * Fehler, ein Wartezustand.
 *
 * Angestossen wird der Durchgang, wenn sich die Liste der offenen Etappen
 * ändert (eine Datei kam dazu) und wenn der Browser wieder online meldet. Nach
 * einem erfolgreichen Durchgang ist die Liste leer, und dann passiert nichts
 * mehr - auch nicht beim nächsten Öffnen der App, denn das Ergebnis liegt im
 * Tagebuch.
 *
 * Ohne Schreibrecht läuft gar nichts: was hier entsteht, wäre eine Änderung am
 * Bestand, und die darf diese Rolle nicht machen.
 */
export function useWeatherNachlauf(): WeatherNachlauf {
  const { trip, diary, setDiary, darfSchreiben } = useRoadbook()
  const fetchWeather = useFetchWeather()
  const online = useOnline()
  const [laeuft, setLaeuft] = useState(false)

  // Der Bestand, wie er zuletzt gelesen wurde. Der Lauf setzt auf dem aktuellen
  // Stand auf und nicht auf dem aus dem Abschluss seines Starts - sonst
  // fragte er nach einer Etappe, die inzwischen einen zweiten Teil bekommen
  // hat, mit den alten Punkten.
  const diaryRef = useRef(diary)
  diaryRef.current = diary
  // Die Reise aus demselben Grund über eine Referenz: der Lauf braucht die
  // Etappen, aber jede Umbenennung einer Etappe stiesse ihn sonst neu an.
  const tripRef = useRef(trip)
  tripRef.current = trip
  const laeuftRef = useRef(false)

  const speichern = useCallback(
    (stageId: string, weather: DiaryWeather) => {
      setDiary(stageId, { weatherFromTrack: weather })
    },
    [setDiary],
  )

  const offeneJobs = useMemo(
    () => (darfSchreiben ? stagesNeedingWeather(activeStages(trip), diary) : []),
    [trip, diary, darfSchreiben],
  )
  // Die Kennung der offenen Liste, damit der Effekt nur anspringt, wenn sich
  // wirklich etwas geändert hat. An den Aufträgen selbst zu hängen hiesse, bei
  // jedem Rendern neu loszulaufen.
  const offeneKennung = offeneJobs.map((j) => j.stageId).join(',')

  const holen = useCallback(
    async (job: WeatherJob) => {
      if (laeuftRef.current) return
      laeuftRef.current = true
      setLaeuft(true)
      try {
        await fetchWeatherForStages([job], { fetchWeather, save: speichern })
      } finally {
        laeuftRef.current = false
        setLaeuft(false)
      }
    },
    [fetchWeather, speichern],
  )

  useEffect(() => {
    if (!darfSchreiben || !online || offeneKennung === '') return
    if (laeuftRef.current) return

    const controller = new AbortController()
    laeuftRef.current = true
    setLaeuft(true)

    void (async () => {
      try {
        const jobs = stagesNeedingWeather(activeStages(tripRef.current), diaryRef.current)
        await fetchWeatherForStages(jobs, {
          fetchWeather,
          save: speichern,
          signal: controller.signal,
        })
      } finally {
        laeuftRef.current = false
        if (!controller.signal.aborted) setLaeuft(false)
      }
    })()

    return () => {
      // Wer die Ansicht verlässt, während der Nachlauf läuft, soll nicht warten
      // müssen. Was schon geholt wurde, liegt im Tagebuch; der Rest kommt beim
      // nächsten Mal.
      controller.abort()
    }
  }, [offeneKennung, online, darfSchreiben, fetchWeather, speichern])

  return { offen: offeneJobs.length, laeuft, holen }
}
